import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { User } from 'app/shared/models/User';
import { Challenge, ChallengeService, Team, TeamMembershipStatus } from 'app/shared/services/challenge.service';
import { UserService } from 'app/shared/services/user.service';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { combineLatest } from 'rxjs/observable/combineLatest';

enum TeamSelectMode {
  TEAMS_OVERVIEW = 'teamsOverview',
  CONFIRM_JOIN = 'confirmJoin',
  TEAM_DETAILS = 'teamDetails',
}

// Information about users that should be invited when creating the team from this component
export interface InvitedUser {
  _id?: string;
  firstName?: string;
  lastName?: string;
  email: string;
}

/**
 * Modal component to select team out of available teams or found an own team and invite members.
 */
@Component({
  selector: 'app-team-select',
  templateUrl: './team-select.component.html',
  styleUrls: ['./team-select.component.scss'],
})
export class TeamSelectComponent implements OnInit, OnDestroy {
  // Currently active navigation step
  public mode: TeamSelectMode = TeamSelectMode.TEAMS_OVERVIEW;

  // The selected team (in confirmation mode and create mode)
  public teamDetails: Team;

  public showTeamCreationDialog: boolean = false;

  // Ids of users which should be invited when team is created
  public invitedUsers: InvitedUser[] = [];

  // The team the user was invited to, based on supplier query param
  private invitedTeamId: string;

  // The challenge requested based on url param
  private requestedChallengeId: string;

  // The founder of the team (in confirmation and create mode)
  public founder;

  // All teams for the challenge
  public teams: Team[];

  // The challenge a team should be selected or created for
  public challenge: Challenge;

  // Currently logged in user
  public currentUser: User;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  public createTeamForm = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.minLength(4), Validators.maxLength(25)]),
  });

  public inviteUserForm = new FormGroup({
    email: new FormControl('', [
      Validators.required,
      Validators.pattern(
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      ),
    ]),
  });

  // Reference to TeamSelectMode to be able to use it in template
  public TeamSelectMode: typeof TeamSelectMode = TeamSelectMode;

  constructor(
    private router: Router,
    private userService: UserService,
    private challengeService: ChallengeService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private route: ActivatedRoute,
  ) {}

  public ngOnInit() {
    this.userService.currentUser.takeUntil(this.ngUnsubscribe).subscribe((user) => {
      if (!user) {
        return;
      }
      this.currentUser = user;
    });

    combineLatest(this.route.params, this.route.queryParams, this.challengeService.displayedChallenge).takeUntil(this.ngUnsubscribe).subscribe(([rParams, qParams, challenge]) => {
      if (rParams.challengeID) {
        this.requestedChallengeId = rParams.challengeID;
      }

      if (qParams.invitedTeamID) {
        this.invitedTeamId = qParams.invitedTeamID;
      }

      let error;
      if (
        (!challenge && this.challengeService.noChallengeFound) ||
        (this.requestedChallengeId && challenge && challenge._id !== this.requestedChallengeId)
      ) {
        console.log(challenge);
        console.log(this.challengeService.noChallengeFound);
        console.log(this.requestedChallengeId);
        this.navigateDashboard();
        return;
      } else if (challenge && moment().isAfter(moment(challenge.start)) && this.invitedTeamId) {
        error = 'CHALLENGE.error.invitationInvalidChallengeAlreadyRunning';
      } else if (challenge && moment().isAfter(moment(challenge.start))) {
        error = 'CHALLENGE.error.preparationPhaseOver';
      }

      if (error) {
        this.translate.get(error).subscribe((text) => {
          this.snackBar.open(text, null, {
            verticalPosition: 'top',
            duration: 6000,
            panelClass: ['b2a-error', 'top'],
          });
        });
        this.navigateDashboard();
      }

      this.challenge = challenge;
    });

    this.challengeService.teamUser
      .withLatestFrom(this.challengeService.teams, this.challengeService.team)
      .takeUntil(this.ngUnsubscribe)
      .subscribe(([teamUser, teams, team]) => {
        if (teams) {
          if (this.invitedTeamId && !teams.find((invitedTeam) => invitedTeam._id === this.invitedTeamId)) {
            this.translate.get('CHALLENGE.error.invitationInvalidTeamNotExisting').subscribe((text) => {
              this.snackBar.open(text, null, {
                verticalPosition: 'top',
                duration: 8000,
                panelClass: ['b2a-error', 'top'],
              });
            });
          }

          // Set status whether currently logged in user has an invitation to the team
          for (const team of teams) {
            const hasInvitation = team.userGroups.invitations.find((user) => user._id === this.currentUser._id);
            team.hasInvitation = !!hasInvitation;
          }

          // Sort teams so that teams where user has invitations to are displayed at top
          const sortedTeams = teams.sort((team1, team2) => {
            if (team1.hasInvitation && !team2.hasInvitation) {
              return -1;
            } else if (!team2.hasInvitation && team1.hasInvitation) {
              return 1;
            } else {
              const team1AtMemberLimit =
                team1.userGroups.members.length + team1.userGroups.invitations.length >= this.challenge.maximumTeamMembers;
              const team2AtMemberLimit =
                team2.userGroups.members.length + team2.userGroups.invitations.length >= this.challenge.maximumTeamMembers;

              if (team1AtMemberLimit && !team2AtMemberLimit) {
                return 1;
              } else if (team2AtMemberLimit && !team1AtMemberLimit) {
                return -1;
              } else {
                return 0;
              }
            }
          });
          this.teams = teams;
        }

        if (team && teamUser.membershipStatus !== TeamMembershipStatus.INVITATION) {
          if (teamUser.isFounder && this.showTeamCreationDialog) {
            this.setTeamDetails(team._id);
            this.mode = TeamSelectMode.TEAM_DETAILS;
          } else {
            setTimeout(() => {
              this.navigateTeamDetails();
            });
          }
        } else {
          this.mode = TeamSelectMode.TEAMS_OVERVIEW;
        }
      });
  }

  /**
   * Sets the currently selected team (required for team creation dialog and confirm join dialog)
   */
  public setTeamDetails(teamID) {
    const foundTeam = this.teams.find((team) => team._id === teamID);
    if (teamID && foundTeam) {
      this.teamDetails = foundTeam;
      this.founder = this.teamDetails.users.find((user) => !!user.isFounder);
    } else {
      this.teamDetails = undefined;
      this.founder = undefined;
    }
  }

  /**
   * Shows the confirm join dialog where the user has to confirm that she wants to join the team
   */
  public async showConfirmJoinDialog(teamID) {
    this.setTeamDetails(teamID);
    this.mode = TeamSelectMode.CONFIRM_JOIN;
  }

  /**
   * Cancels the confirm join team dialog
   */
  public async cancelDialog() {
    this.setTeamDetails(undefined);
    this.mode = TeamSelectMode.TEAMS_OVERVIEW;
  }

  /**
   * Cancels the team creation dialog
   */
  public cancelTeamCreation() {
    if (this.challenge._id && this.teamDetails) {
      this.challengeService.deleteTeam(this.challenge._id, this.teamDetails._id).then(() => {
        this.invitedUsers = [];
        this.setTeamDetails(undefined);
        this.showTeamCreationDialog = false;
        this.mode = TeamSelectMode.TEAMS_OVERVIEW;
      });
    }
  }

  /**
   * Adds the user to the currently selected team
   */
  public applyForTeam() {
    this.challengeService.applyForTeam(this.challenge._id, this.teamDetails._id).catch(() => {
      this.translate.get('CHALLENGE.error.general').subscribe((text) => {
        this.snackBar.open(text, null, {
          verticalPosition: 'top',
          duration: 4000,
          panelClass: ['b2a-error', 'top'],
        });
        this.challengeService.reloadTeams();
      });
    });
  }

  /**
   * Opens the team creation dialog
   */
  public async createTeam() {
    if (this.challenge) {
      this.showTeamCreationDialog = true;
      this.challengeService
        .createTeam(this.challenge._id, this.createTeamForm.value)
        .then((createdTeam) => {
          this.setTeamDetails(createdTeam._id);
          this.mode = TeamSelectMode.TEAM_DETAILS;
        })
        .catch((error) => {
          const errorObject = error.body.error;
          
          if (errorObject.startsWith('maximum_challenge_members')){
            this.translate.get('CHALLENGE.error.maximumChallengeMemberReached').subscribe((text) => {
              this.snackBar.open(text, null, {
                verticalPosition: 'top',
                duration: 4000,
                panelClass: ['b2a-error', 'top'],
              });
            });
          } else if (errorObject.startsWith('name_conflict')) {
            this.translate.get('CHALLENGE.error.conflictingTeamName').subscribe((text) => {
              this.snackBar.open(text, null, {
                verticalPosition: 'top',
                duration: 4000,
                panelClass: ['b2a-error', 'top'],
              });
            });
          }
        });
    }
  }

  /**
   * Called when finishing the team create assistant and all invitations are sent out
   */
  public async finishTeamCreation() {
    this.challengeService
      .inviteUsers(
        this.challenge._id,
        this.teamDetails._id,
        this.invitedUsers.map((user) => user.email),
      )
      .then()
      .catch(() => {
        this.translate.get('CHALLENGE.error.general').subscribe((text) => {
          this.snackBar.open(text, null, {
            verticalPosition: 'top',
            duration: 4000,
            panelClass: ['b2a-error', 'top'],
          });
        });
        this.challengeService.reloadTeams();
      });
    this.navigateTeamDetails();
  }

  /**
   * Invites a user to the team
   */
  public inviteUserToTeam() {
    this.challengeService
      .checkUserEligibility(this.challenge._id, this.teamDetails._id, this.inviteUserForm.value.email)
      .then((result: InvitedUser) => {
        const userAlreadyInvited = this.invitedUsers.find((user) => {
          return user.email === result.email;
        });

        if (!userAlreadyInvited) {
          this.invitedUsers.push(result);
        }
        this.inviteUserForm.reset();
      })
      .catch((error) => {
        const errorObject = error.body.error.error;

        if (errorObject.startsWith('invalid_domain')) {
          this.translate.get('CHALLENGE.error.invalidInvitationDomain').subscribe((text) => {
            this.snackBar.open(text, null, {
              verticalPosition: 'top',
              duration: 4000,
              panelClass: ['b2a-error', 'top'],
            });
          });
        } else if (errorObject.startsWith('maximum_challenge_members')){
          this.translate.get('CHALLENGE.error.maximumChallengeMemberReached').subscribe((text) => {
            this.snackBar.open(text, null, {
              verticalPosition: 'top',
              duration: 4000,
              panelClass: ['b2a-error', 'top'],
            });
          });
        } else {
          this.translate.get('CHALLENGE.error.userInviteFailed').subscribe((text) => {
            this.snackBar.open(text, null, {
              verticalPosition: 'top',
              duration: 4000,
              panelClass: ['b2a-error', 'top'],
            });
          });
        }
      });
  }

  /**
   * Accepts an invitation to the team
   */
  public acceptInvitation(teamID) {
    this.challengeService
      .acceptInvitation(this.challenge._id, teamID, this.currentUser._id)
      .then()
      .catch((error) => {
        const errorObject = error.body.error.error;

        if (errorObject.startsWith('maximum_challenge_members')){
          this.translate.get('CHALLENGE.error.maximumChallengeMemberReached').subscribe((text) => {
            this.snackBar.open(text, null, {
              verticalPosition: 'top',
              duration: 4000,
              panelClass: ['b2a-error', 'top'],
            });
          });
        } else {
          this.translate.get('CHALLENGE.error.general').subscribe((text) => {
            this.snackBar.open(text, null, {
              verticalPosition: 'top',
              duration: 4000,
              panelClass: ['b2a-error', 'top'],
            });
          });
          location.reload(); 
        }
      });
  }

  /**
   * Deny invitation to a team
   */
  public denyInvitation(teamID) {
    this.challengeService
      .declineInvitation(this.challenge._id, teamID, this.currentUser.email)
      .then()
      .catch(() => {
        this.translate.get('CHALLENGE.error.general').subscribe((text) => {
          this.snackBar.open(text, null, {
            verticalPosition: 'top',
            duration: 4000,
            panelClass: ['b2a-error', 'top'],
          });

          location.reload();
        });
      });
  }

  /**
   * Called by team founder to cancel an invitation of the user to the team
   */
  public cancelInvitation(email) {
    const userIndex = this.invitedUsers.findIndex((user) => user.email === email);
    if (userIndex !== -1) {
      this.invitedUsers.splice(userIndex, 1);
    }
  }

  public navigateTeamDetails(): void {
    this.router.navigate(['stats']).then();
  }

  public navigateDashboard(): void {
    this.router.navigate(['/dashboard']).then();
  }

  /**
   * On destroy cancel active subscriptions
   */
  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3658806670/src/client/web-client/src/app/pages/team-select/team-select.component.ts